import { Button } from '@/components/_ui/Button'
import Checkbox from '@/components/_ui/Checkbox'
import { Input } from '@/components/_ui/Input'
import Spinner from '@/components/_ui/Spinner'
import T, { useTranslate } from '@/components/Helpers/Translate'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import type { SignInPossiblePagesType } from '@/lib/hooks/services/useSignInUser'
import { useSignInUser } from '@/lib/hooks/services/useSignInUser'

import { useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { mixpanelSigninEventLocation } from '@/lib/services/mixpanel/helpers/eventLocation'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import PasswordInput from '../_helpers/Components/PasswordInput'

type Inputs = {
  email: string
  password: string
  keepMeLoggedIn: boolean
}

type LoginFormProps = {
  redirectToAfterLogin?: string
  onLoginSuccess?: () => void
  page?: SignInPossiblePagesType
}

const LoginForm = ({
  redirectToAfterLogin,
  onLoginSuccess,
  page,
}: LoginFormProps) => {
  const { gtm } = useGTM()

  const [errorCode, setErrorCode] = useState<number | string | null>(null)
  const { signInUser, isLoadingSignIn } = useSignInUser({
    isSignInAfterAccountCreation: false,
  })
  const { clientRedirect, queryParamsRedirect } = useClientRedirect()
  const { translate } = useTranslate()
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()

  const [loginError, setLoginError] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    mode: 'onBlur',
  })

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const customer = await signInUser({
      email: data.email,
      password: data.password,
      keepSignedIn: data.keepMeLoggedIn,
      registered: true,
      page,
    })

    if (!customer || customer.error) {
      if (customer?.error?.code) {
        setErrorCode(customer.error.code)

        gtm.signInServerError({ error: customer })
        mixpanelService
          .trackEvent(MixpanelEvent.SIGN_IN_SERVER_ERROR, {
            event_location: mixpanelSigninEventLocation(page),
            response_code: customer.error.code.toString(),
            error_message: customer.error.message || '',
          })
          .catch(() => {})
      }
      setLoginError(true)
      return
    }

    if (onLoginSuccess) {
      onLoginSuccess()
    }

    if (queryParamsRedirect) {
      // If there's a redirectTo query param in the url at the time of login,
      // redirect to that path.
      await clientRedirect.toQueryParams()
    } else if (redirectToAfterLogin) {
      // If no queryParam, fall back to the redirectToAfterLogin prop.
      await clientRedirect.to({ path: redirectToAfterLogin })
    }
  }

  const getErrorMessage = (): string => {
    if (errorCode === 401) {
      return translate('login_or_password_is_incorrect')
    }

    return translate('generic_error')
  }

  return (
    <form
      className="my-10 grid grid-cols-1 gap-2 gap-y-5 sm:grid-cols-1"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        error={errors.email?.message ?? loginError}
        {...register('email', {
          required: translate('this_field_is_required'),
          onChange: () => {
            if (loginError) setLoginError(false)
          },
        })}
        label={translate('email_or_unicity_id')}
        data-test="login-email-input"
      />
      <PasswordInput
        error={loginError && getErrorMessage()}
        {...register('password', {
          required: translate('this_field_is_required'),

          minLength: {
            value: 6,
            message: translate('password_six_character_min'),
          },
          onChange: () => {
            if (loginError) setLoginError(false)
          },
        })}
        withPasswordReveal
        label={translate('password')}
        data-test="password-email-input"
      />
      <Checkbox id="keep-me-logged-in" data-test="login-keep-me-checkbox">
        <T>keep_logged_in</T>
      </Checkbox>
      <div className="mt- mt-10 flex flex-col items-center">
        <Button
          className="w-full max-w-[300px] rounded-full text-white"
          size="lg"
          variant="secondary"
          type="submit"
          disabled={isLoadingSignIn}
          onClick={() => {
            gtm.signInClicked()
            mixpanelService
              .trackEvent(MixpanelEvent.SIGN_IN_CLICKED, {
                event_location: mixpanelSigninEventLocation(page),
              })
              .catch(() => {})
          }}
          data-test="login-submit-button"
        >
          {isLoadingSignIn ? <Spinner /> : translate('login')}
        </Button>
        <a
          className="mt-3 text-sm text-primary underline"
          href={parseLinkUrlWithLocale({ url: '/reset-password' })}
          data-test="forgot-password-button"
        >
          <T>forgot_password</T>
        </a>
      </div>
    </form>
  )
}
export default LoginForm
